import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { passkeyApi } from '@api/client';
import { useAuth } from '@hooks/useAuth';
import type { Passkey } from '@api/types';

const passkeysSupported = typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

export function PasskeysCard() {
  const qc = useQueryClient();
  const { registerPasskey } = useAuth();
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: passkeys = [], isLoading } = useQuery<Passkey[]>({
    queryKey: ['passkeys', 'me'],
    queryFn: () => passkeyApi.list(),
    enabled: passkeysSupported,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => passkeyApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['passkeys', 'me'] }),
  });

  async function handleAdd() {
    setError(null);
    setRegistering(true);
    try {
      const input = window.prompt('Nombre para este dispositivo (opcional)', 'Mi dispositivo')?.trim();
      const trimmed = typeof input === 'string' && input.length > 0 ? input : undefined;
      await registerPasskey(trimmed);
      await qc.invalidateQueries({ queryKey: ['passkeys', 'me'] });
    } catch {
      setError('No se pudo registrar el passkey. Cancelado o no soportado.');
    } finally {
      setRegistering(false);
    }
  }

  function handleDelete(p: Passkey) {
    if (!window.confirm(`¿Eliminar el passkey "${p.name ?? 'sin nombre'}"? Ya no podrás entrar con él.`)) return;
    deleteMutation.mutate(p.id);
  }

  if (!passkeysSupported) return null;

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">Mis dispositivos (passkey)</span>
      </div>
      <div style={{ padding: '12px 18px 16px' }}>
        <p style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginBottom: 12, lineHeight: 1.5 }}>
          Los passkeys te permiten entrar al panel sin recibir emails — usa tu huella, Face ID o el método
          de seguridad que tenga este dispositivo.
        </p>

        {isLoading && <p style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Cargando…</p>}

        {!isLoading && passkeys.length === 0 && (
          <p style={{ fontSize: 13, color: 'var(--fg-soft)', fontStyle: 'italic', marginBottom: 12 }}>
            Aún no has añadido ningún passkey.
          </p>
        )}

        {!isLoading && passkeys.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {passkeys.map((p) => (
              <li
                key={p.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  background: 'var(--bg-soft)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 13,
                }}
              >
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontWeight: 500 }}>{p.name ?? 'Sin nombre'}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                    {p.deviceType === 'singleDevice' ? 'Solo este dispositivo' : 'Sincronizado'} · añadido el {fmtDate(p.createdAt)}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn"
                  data-variant="ghost"
                  data-size="sm"
                  onClick={() => { handleDelete(p); }}
                  disabled={deleteMutation.isPending}
                  title="Eliminar passkey"
                >
                  Eliminar
                </button>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          className="btn"
          data-variant="primary"
          data-size="sm"
          onClick={() => { void handleAdd(); }}
          disabled={registering}
        >
          {registering ? 'Registrando…' : 'Añadir passkey'}
        </button>

        {error && (
          <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 8 }}>{error}</p>
        )}
      </div>
    </div>
  );
}
